
import {
  inject,
  defineComponent,
  computed, ComputedRef, watch, ref,
} from 'vue';
import BadgeDirective from 'primevue/badgedirective';
import { TenantAttributes, TypeCampaignEnum, DataExplorerDataType } from '@/types';
import { useStore } from '@/store';
import MySitesMenu from '@/components/header/MySitesMenu.vue';
import MyNotificationsMenu from '@/components/header/MyNotificationsMenu.vue';
import MyProfileMenu from '@/components/header/MyProfileMenu.vue';
import MyStoreMenu from '@/components/header/MyStoreMenu.vue';
import Button from 'primevue/button';
import { setLocalStorageElement } from '@/helpers/LocalStorage';
import { findPermission, UserState } from '@/composables/User';
import { UserTypeEnum } from '@/types/enums';
import { activateCampaign, AutomatedScenarioState } from '@/composables/AutomatedScenarios';
import Dropdown from 'primevue/dropdown';
import GlobalDataSourceSelector from '@/components/header/GlobalDataSourceSelector.vue';
import InputSwitch from 'primevue/inputswitch';
import { activate as activateTemplate, TemplateEditorState } from '@/composables/template-editor/TemplateEditor';
import { useI18n } from 'vue-i18n';
import type { OpenFlow } from '@/store/modules/flow-editor';
import { isSandboxAccount, isDemoModeActive, toggleDemoMode, showDemoSettings } from '@/composables/DemoMode';
import DemoSettingsDialog from '@/components/header/DemoSettingsDialog.vue';

export default defineComponent({
  name: 'Header',

  directives: {
    badge: BadgeDirective,
  },

  components: {
    Button,
    MyStoreMenu,
    MyProfileMenu,
    MyNotificationsMenu,
    MySitesMenu,
    Dropdown,
    GlobalDataSourceSelector,
    InputSwitch,
    DemoSettingsDialog,
  },

  props: {
    isMobile: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  setup() {
    const { t } = useI18n();
    const tenantAttributes: TenantAttributes | undefined = inject('tenantAttributes');
    const store = useStore();
    const isSidebarExpanded = computed(() => store.state.general.isSidebarExpanded);
    const buttonIcon: ComputedRef<string> = computed(() => (isSidebarExpanded.value ? 'fa-light fa-bars-staggered' : 'fa-light fa-bars'));

    const openedDatas = computed(() => store.getters['dataExplorer/getOpenedDatas']);
    const minimizedFlows = computed<OpenFlow[]>(() => store.getters['flowEditor/getMinimizedFlows']);

    const fastGoProductSelected: any = ref(null);
    const fastGoProductItems = computed(() => {
      // Separate minimized flows by type
      const minimizedAutomation = minimizedFlows.value
        .filter((f: OpenFlow) => f.type === TypeCampaignEnum.AUTOMATION)
        .map((f: OpenFlow) => ({
          label: f.name || `Campagne n${f.id}`,
          id: f.id,
          type: 'flow-automation',
        }));
      const minimizedBulk = minimizedFlows.value
        .filter((f: OpenFlow) => f.type === TypeCampaignEnum.BULK)
        .map((f: OpenFlow) => ({
          label: f.name || `Campagne n${f.id}`,
          id: f.id,
          type: 'flow-bulk',
        }));

      const products = [
        {
          label: t('header.automationCampaigns'),
          code: 'AC',
          items: [
            ...minimizedAutomation,
            ...AutomatedScenarioState.automationCampaigns.map((campaign) => ({
              label: campaign.scenario.settingsData.name,
              id: campaign.scenario.id,
              type: TypeCampaignEnum.AUTOMATION,
            })),
          ],
        },
        {
          label: t('header.bulkCampaigns'),
          code: 'BC',
          items: [
            ...minimizedBulk,
            ...AutomatedScenarioState.bulkCampaigns.map((campaign) => ({
              label: campaign.scenario.settingsData.name,
              id: campaign.scenario.id,
              type: TypeCampaignEnum.BULK,
            })),
          ],
        },
        {
          label: t('header.templates'),
          code: 'TL',
          items: TemplateEditorState.templates.map((template) => ({
            label: template.informations.name,
            id: template.id,
            type: 'template',
          })),
        },
        {
          label: t('header.dataSheets'),
          code: 'DE',
          items: openedDatas.value.map((openedData: any) => ({
            label: t(`dataExplorer.tabs.${openedData.dataType}`, [openedData.tabLabel]),
            id: openedData.dataId,
            type: openedData.dataType,
          })),
        }
      ];
      return products.filter((product) => product.items.length > 0);
    });

    watch(fastGoProductSelected, async () => {
      if (fastGoProductSelected.value && fastGoProductSelected.value?.id && fastGoProductSelected.value?.type) {
        // Handle minimized flows from new editor
        if (fastGoProductSelected.value?.type === 'flow-automation') {
          store.dispatch('flowEditor/restoreFlowWithConflictCheck', { id: fastGoProductSelected.value.id, type: TypeCampaignEnum.AUTOMATION });
          fastGoProductSelected.value = null;
          return;
        }
        if (fastGoProductSelected.value?.type === 'flow-bulk') {
          store.dispatch('flowEditor/restoreFlowWithConflictCheck', { id: fastGoProductSelected.value.id, type: TypeCampaignEnum.BULK });
          fastGoProductSelected.value = null;
          return;
        }

        // Handle old editor campaigns
        if ([TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK].includes(fastGoProductSelected.value?.type)) {
          await activateCampaign(fastGoProductSelected.value?.id, fastGoProductSelected.value?.type);
        }

        if (fastGoProductSelected.value?.type === 'template') {
          await activateTemplate(fastGoProductSelected.value?.id, '', '');
        }

        if (
          [
            DataExplorerDataType.ADDRESS_FILE,
            DataExplorerDataType.CART_FILE,
            DataExplorerDataType.CONTACT_FILE,
            DataExplorerDataType.ORDER_FILE,
            DataExplorerDataType.CUSTOM_DATA_FILE,
          ].includes(fastGoProductSelected.value?.type)
        ) {
          store.dispatch('dataExplorer/switchData', { dataId: fastGoProductSelected.value?.id });
        }


        fastGoProductSelected.value = null;
      }
    });

    const toggleSidebar = () => {
      setLocalStorageElement('spm_is_sidebar_expanded', Number(!isSidebarExpanded.value));
      store.commit('general/setIsSidebarExpanded', !isSidebarExpanded.value);
    };

    const canAddShop = computed(() => UserState.user.userType === UserTypeEnum.ADMIN);

    return {
      toggleSidebar,
      tenantAttributes,
      buttonIcon,
      findPermission,
      fastGoProductSelected,
      fastGoProductItems,
      canAddShop,
      isSandboxAccount,
      isDemoModeActive,
      toggleDemoMode,
      showDemoSettings,
    };
  },
});
